import { useTranslation } from 'next-i18next';
import { type ClassGroupRes } from '@/api/gen/_model/classGroupRes';
import { LoadingSpinner } from '@/shared/components';
import { CLASSROOM_NAME_MAX_LENGTH } from '@/shared/utils/classroomValidation';
import { Button, DialogBody, DialogFooter, TextField } from '@/ui_v3';
import { ClassListItem } from './ClassListItem';

/** 「내 클래스」 탭: 체크 목록 + 「저장」. */
export function MyClassroomsPanel({
  classrooms,
  checked,
  isPending,
  canSave,
  onToggle,
  onSave
}: {
  classrooms: ClassGroupRes[];
  checked: Set<string>;
  isPending: boolean;
  canSave: boolean;
  onToggle: (classGroupId: string) => void;
  onSave: () => Promise<void>;
}) {
  const {
    t
  } = useTranslation(['common']);
  return <>
      <DialogBody className="flex min-h-0 flex-1 flex-col px-md pb-md mobile-lg:px-lg" data-sentry-element="DialogBody" data-sentry-source-file="ClassroomLinkPanels.tsx">
        <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-t-md bg-neutral">
          <ul className="flex min-h-0 flex-1 flex-col gap-2xs overflow-y-auto py-sm pl-sm pr-xs">
            {classrooms.map(classroom => <ClassListItem key={classroom.classGroupId} classroom={classroom} isChecked={checked.has(classroom.classGroupId)} disabled={isPending} linkedLabel={t('common:classroomLink.linkedChip')} memberCountLabel={t('common:classroomLink.memberCount', {
            count: classroom.memberCount
          })} onToggle={() => onToggle(classroom.classGroupId)} />)}
          </ul>
          {/*
            목록 아래를 흰색으로 흐린다. 이미지(끝 알파 98%)는 회색 배경이 맨 아래 줄로 비쳐서
            끝까지 불투명한 CSS 그라데이션을 쓰고, 소수점 높이에서 틈이 생기지 않게 1px 아래까지 덮는다.
           */}
          <div aria-hidden className="pointer-events-none absolute inset-x-0 -bottom-px h-[21px] bg-gradient-to-b from-white/0 to-white" />
        </div>
      </DialogBody>
      <SubmitFooter label={t('common:classroomLink.saveButton')} canSave={canSave} isPending={isPending} onClick={onSave} data-sentry-element="SubmitFooter" data-sentry-source-file="ClassroomLinkPanels.tsx" />
    </>;
}

/** 「새로 만들기」 탭: 이름 입력 + 「만들고 연결하기」. */
export function CreateClassroomPanel({
  name,
  isPending,
  canSave,
  onNameChange,
  onSave
}: {
  name: string;
  isPending: boolean;
  canSave: boolean;
  onNameChange: (name: string) => void;
  onSave: () => Promise<void>;
}) {
  const {
    t
  } = useTranslation(['common']);
  return <>
      <DialogBody className="flex min-h-0 flex-1 flex-col px-md pb-md mobile-lg:px-lg" data-sentry-element="DialogBody" data-sentry-source-file="ClassroomLinkPanels.tsx">
        <div className="flex flex-1 flex-col rounded-md bg-neutral px-md py-xl">
          <TextField intent="strong" required autoFocus title={t('common:classroomLink.classNameLabel')} helperText={t('common:classroomLink.createHelp')} placeholder={t('common:classroomLink.classNamePlaceholder')} value={name} disabled={isPending} maxLength={CLASSROOM_NAME_MAX_LENGTH} onChange={e => onNameChange(e.target.value.slice(0, CLASSROOM_NAME_MAX_LENGTH))} onKeyDown={e => {
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            if (canSave) void onSave();
          }
        }} data-sentry-element="TextField" data-sentry-source-file="ClassroomLinkPanels.tsx" />
        </div>
      </DialogBody>
      <SubmitFooter label={t('common:classroomLink.createAndLinkButton')} canSave={canSave} isPending={isPending} onClick={onSave} data-sentry-element="SubmitFooter" data-sentry-source-file="ClassroomLinkPanels.tsx" />
    </>;
}
function SubmitFooter({
  label,
  canSave,
  isPending,
  onClick
}: {
  label: string;
  canSave: boolean;
  isPending: boolean;
  onClick: () => Promise<void>;
}) {
  return <DialogFooter className="pt-0 mobile-lg:pt-0" data-sentry-element="DialogFooter" data-sentry-component="SubmitFooter" data-sentry-source-file="ClassroomLinkPanels.tsx">
      <Button variant="contained" intent="strong" size="lg" className="w-full" disabled={!canSave || isPending} leftSlot={isPending ? <LoadingSpinner /> : undefined} onClick={() => void onClick()} data-sentry-element="Button" data-sentry-source-file="ClassroomLinkPanels.tsx">
        {label}
      </Button>
    </DialogFooter>;
}